/* =============================================================================
   YOJO Motion — Intersection Observer that toggles .is-visible on [data-motion]
   and [data-motion-stagger] elements when they enter the viewport.
   Added 2026-04-17 (audit wave D3b, P7-009).
   ============================================================================ */

(function () {
  if (typeof window === 'undefined') return;

  // Respect reduced-motion immediately — reveal everything once and exit.
  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    document.querySelectorAll('[data-motion], [data-motion-block], [data-motion-stagger]').forEach(function (el) {
      el.classList.add('is-visible');
    });
    return;
  }

  if (!('IntersectionObserver' in window)) {
    // Old browser — reveal immediately, no JS-driven motion.
    document.querySelectorAll('[data-motion], [data-motion-block], [data-motion-stagger]').forEach(function (el) {
      el.classList.add('is-visible');
    });
    return;
  }

  /* ── Release 2, Outcome B: one governed lifecycle ────────────────────────
     Every listener this module attaches — document-level and per-image — is
     bound to ONE AbortController, so destroy() removes all of them with a
     single abort(). Same pattern as assets/yojo-shop.js, which measured clean
     (10 observers constructed, 10 disconnected, net live 0).
     The two IntersectionObservers below are module singletons: observe() on an
     already-observed element is a no-op, so they never accumulate. destroy()
     disconnects them anyway so a theme-editor teardown is complete.
     NOTHING VISUAL CHANGES HERE — thresholds, classes, the 4s failsafe and all
     timing are exactly as before. */
  var REG = (window.__yojoMotion = window.__yojoMotion || {});
  REG.ac = REG.ac || new AbortController();
  var MOTION_AC = REG.ac;

  // Failsafe handshake (2026-07-05): the hidden state in yojo-motion.css only
  // applies under html.yojo-motion-on. If this script never executes, every
  // tagged surface stays fully visible instead of invisible.
  //
  // Late-boot guard (2026-08-05 audit P1): if this script lands well after
  // first paint (slow network / delayed JS), arming the class HIDES content
  // the user is already reading — measured: 7 above-fold cards blanked for
  // ~3.6s at a 3s JS delay. Content the user has seen must never disappear:
  // when boot is late, pre-mark everything currently in (or near) the
  // viewport as revealed IN THE SAME TASK that arms the hidden state, so
  // those elements never render a hidden frame. Below-fold elements still
  // get their scroll reveal.
  var LATE_BOOT = (performance.now && performance.now() > 1500);
  if (LATE_BOOT) {
    var vh = window.innerHeight || 0;
    document.querySelectorAll('[data-motion], [data-motion-block], [data-motion-stagger]').forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < vh + 100 && r.bottom > -100) el.classList.add('is-visible');
    });
  }
  document.documentElement.classList.add('yojo-motion-on');

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    root: null,
    rootMargin: '0px',
    // C-17 (2026-08-01, Motion + Mobile Spec v2): 0 (was C-10's 0.35).
    // Deep reference re-read found every per-card call site on the reference
    // explicitly passes threshold:0 — cards should start revealing the
    // instant any pixel enters the viewport. Fires once, then unobserve()s
    // (below) — no repeat-on-scroll-back-up behavior.
    threshold: 0,
  });

  // C-17 — second, coarser tier for hero / PDP blocks / editorial rows.
  // Opt-in via [data-motion-block] (see yojo-motion.css). Kept as a fully
  // separate IntersectionObserver instance/threshold from the card tier
  // above — do not merge them, the two tiers are deliberately different.
  var blockObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        blockObserver.unobserve(entry.target);
      }
    });
  }, {
    root: null,
    rootMargin: '0px',
    threshold: 0.4,
  });

  /* Tall-block tier (2026-08-14, Shir feedback round 2): a threshold-0.4
     observer needs 40% of the ELEMENT inside the viewport, so any block
     taller than 2.5 viewports can never fire — the catalog directory's two
     groups (each several screens tall on mobile) sat at opacity:0 until the
     4s failsafe, filmed by the owner's reviewer as a blank "loading" void.

     Follow-up (same day, measured on LIVE after the first deploy): the first
     cut only rerouted blocks whose 40% exceeded the viewport, but a 1,844px
     group at 844px viewport (40% = 738px) kept the 0.4 tier and still could
     not fire at the top of the page, where layout above it caps its visible
     share at ~24% — the reviewer's exact scroll position. The preview pass
     was a false positive via the late-boot pre-mark. Correct rule: the 0.4
     tier only makes sense for blocks a viewport can mostly contain; anything
     taller than ~3/4 of a viewport reveals on first pixel. */
  var tallBlockObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        tallBlockObserver.unobserve(entry.target);
      }
    });
  }, {
    root: null,
    rootMargin: '0px',
    threshold: 0,
  });

  REG.observer = observer;
  REG.blockObserver = blockObserver;
  REG.tallBlockObserver = tallBlockObserver;

  function observeAll() {
    document.querySelectorAll('[data-motion]:not(.is-visible), [data-motion-stagger]:not(.is-visible)').forEach(function (el) {
      observer.observe(el);
    });
    var vh = window.innerHeight || 0;
    document.querySelectorAll('[data-motion-block]:not(.is-visible)').forEach(function (el) {
      var h = el.getBoundingClientRect().height;
      if (h > vh * 0.75) tallBlockObserver.observe(el);
      else blockObserver.observe(el);
    });
  }

  /* R3 fix (2026-08-02, adversarial P1-4): the second IIFE's refresh() called
     observeAll(), which lives in THIS closure — an uncaught ReferenceError on
     every dynamic-content event, so injected content was never re-observed.
     The two IIFEs already share state through window.__yojoMotion; the
     function is published there too. */
  REG.observeAll = observeAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAll, { once: true });
  } else {
    observeAll();
  }

  // Re-observe anything injected later (e.g., cart drawer, product-recommendations).
  document.addEventListener('shopify:section:load', observeAll, { signal: MOTION_AC.signal });
  document.addEventListener('yojo:content-added', observeAll, { signal: MOTION_AC.signal });

  // Gate D1 (2026-08-02): <product-recommendations> (assets/component-product-form.js)
  // fetches its content via IntersectionObserver + fetch(), then sets
  // this.innerHTML and dispatches a plain `new Event('product-recommendations-loaded')`
  // on ITSELF — bubbles:false, so a normal document-level bubble listener never
  // sees it. Any [data-motion] card rendered inside that AJAX'd markup (PDP
  // recommendations shelf) would otherwise never get observed and stay at
  // opacity:0 forever. Capture-phase listeners still receive non-bubbling
  // events during the capture pass, so this works without touching the
  // vendored custom-element code.
  document.addEventListener('product-recommendations-loaded', observeAll, { capture: true, signal: MOTION_AC.signal });

  // Wave2 failsafe (atlas D7): content visibility beats animation. Whatever
  // has not revealed within 4s becomes visible unconditionally, so no surface
  // can ever read as a blank band.
  setTimeout(function () {
    document.querySelectorAll('[data-motion]:not(.is-visible), [data-motion-block]:not(.is-visible), [data-motion-stagger]:not(.is-visible)').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }, 4000);
})();

/* =============================================================================
   YOJO Motion — Runtime extensions (2026-05-10 Wave 4)
   Provides:
     - Image fade-in on lazy-load (toggles .is-loaded on lazy imgs)
   Auto-tagging was REMOVED in Wave 4 (2026-05-10). Reason: brittle
   selector inference + caused a P0 visibility regression (PR #29 → #30).
   Motion is now explicit-only: every animated surface tags itself with
   data-motion or data-motion-stagger in its Liquid. Lock-lint rule L2.2
   forbids JS-side re-introduction of motion attribute setting.

   Parallax runtime — REMOVED (recovery #7, 2026-08-01). initParallax() drove
   [data-motion="parallax"] / [data-yojo-parallax], but the only markup that
   ever emitted those attributes was sections/yojo-paletas-banner.liquid, and
   no template renders that section (fully orphaned). Dead code, and it
   contradicted the "no parallax" motion rule (16c-recovery-audit.md #7,
   which flagged it as dormant-not-deleted). Re-introduction requires a real
   template reference proven first — see yojo-motion.css for the matching
   removed CSS base rule.
   ============================================================================ */

(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  /* Shared with the observer IIFE above via window.__yojoMotion — the two IIFEs
     have separate closures, so the AbortController and the observers must be
     resolved from one registry rather than redeclared. */
  var REG = (window.__yojoMotion = window.__yojoMotion || {});
  REG.ac = REG.ac || new AbortController();
  var MOTION_AC = REG.ac;

  var prefersReduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReduced) {
    document.documentElement.classList.add('yojo-motion-reduced');
    return;
  }

  /* Header scroll behavior — REMOVED 2026-05-10 Wave 2.
     Theme's <main-header> is position: relative. The function correctly
     guarded against non-sticky headers and returned early — meaning it
     never produced visible behavior on this theme. Dead code that reads
     as "feature but no-op" is worse than no code. Forbidden by lock-lint
     L2.1 — re-introduction requires sticky-header-first proof. */

  /* ---------- 1. Image fade-in on lazy-load ---------- */
  /* IDEMPOTENT (Release 2, Outcome B). This ran unguarded on every
     shopify:section:load and yojo:content-added, re-adding a load+error pair to
     every lazy image that had not finished loading. {once:true} only removes a
     listener once it FIRES, so an image that is never scrolled into view keeps
     every pair ever attached. Measured before the fix: 10 shopify:section:load
     events added 40 listeners and removed 2 — 38 unmanaged listeners, ~3.8 per
     event, attributed to this function by stack trace.

     data-yojo-fade-bound marks an image as already wired. Purely additive: no
     class, timing or visual behaviour changes. */
  function initImageFade(root) {
    (root || document).querySelectorAll('img[loading="lazy"]:not([data-yojo-fade-bound])').forEach(function (img) {
      // Skip critical / above-fold images that mark themselves explicitly
      if (img.hasAttribute('data-yojo-no-fade')) return;
      /* reference-parity 2026-08-05 (owner: scrolling through products still
         doesn't feel like the reference): PRODUCT imagery never fades in —
         measured on the reference site, commerce images simply appear. The fade
         stays for editorial/blog/hero imagery only. */
      if (img.closest('.yhp-media, .yhp-card, .yojo-grid, [data-rail-scroller]')) return;
      img.setAttribute('data-yojo-fade-bound', '');
      img.classList.add('yojo-img-fade-in');
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        var done = function () { img.classList.add('is-loaded'); };
        img.addEventListener('load',  done, { once: true, signal: MOTION_AC.signal });
        img.addEventListener('error', done, { once: true, signal: MOTION_AC.signal });
      }
    });
  }

  /* Marquee duplication REMOVED 2026-08-05 (motion plan #2): 0 call sites —
     the CSS half went from yojo-motion.css in the same pass. */

  function init() {
    initImageFade();
    document.documentElement.classList.add('yojo-motion-extended');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* refresh(root) — rescan a subtree after dynamic content arrives. Scoped to
     the section that actually changed rather than the whole document, so a
     theme-editor session does not re-walk every image on the page per event. */
  function refresh(root) {
    initImageFade(root);
    if (typeof REG.observeAll === 'function') REG.observeAll();
  }

  /* destroy() — complete teardown: one abort() removes every listener this
     module attached (document-level and per-image), and both observers are
     disconnected. Idempotent. */
  function destroy() {
    MOTION_AC.abort();
    try { if (REG.observer) REG.observer.disconnect(); } catch (e) {}
    try { if (REG.blockObserver) REG.blockObserver.disconnect(); } catch (e) {}
    try { if (REG.tallBlockObserver) REG.tallBlockObserver.disconnect(); } catch (e) {}
    document.documentElement.classList.remove('yojo-motion-extended');
  }

  document.addEventListener('shopify:section:load', function (e) {
    refresh(e && e.target && e.target.querySelectorAll ? e.target : document);
  }, { signal: MOTION_AC.signal });

  document.addEventListener('yojo:content-added', function (e) {
    var d = e && e.detail;
    refresh(d && d.container && d.container.querySelectorAll ? d.container : document);
  }, { signal: MOTION_AC.signal });

  /* Public lifecycle contract, matching window.YojoShop. */
  window.YojoMotion = { init: init, refresh: refresh, destroy: destroy };
})();
